import Sentry from "@mirage/shared/sentry";
import { isErrorWithMessage } from "@mirage/shared/util/error";
import { isEqualWith } from "lodash";
import type { Observable } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { convertToConversationMessage } from "../utils/convertConversationMessages";

import { createConversationDiagnostics } from "@mirage/service-conversation/service/conversationDiagnostics";
import { createLoggedConversationResponse } from "@mirage/service-conversation/service/conversationsLogger/conversationResponseLogger";
import { parseMessageJSON } from "@mirage/service-conversation/service/conversationsSync/jsonMessageParsers";
import { createConversationsSyncWithAdapters } from "@mirage/service-conversation/service/conversationsSync/sync";
import {
  createPendingMessages,
  getErrorConversationsResponseMessages,
  getInProgressConversationsResponseMessages,
  getPendingMessagesResponseArgs,
} from "@mirage/service-conversation/service/processPostNewMessage";
import type { APIv2GRPCWebError } from "@mirage/service-dbx-api/service/grpc/api_v2";
import type { ConversationsResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { tagged } from "@mirage/service-logging";
import type { GuaranteedKVStorage } from "@mirage/storage";
import type {
  Conversation,
  ConversationAssistantMessage,
  ConversationMessage,
  ConversationMetadata,
  ConversationUserMessage,
  ConversationWithId,
  NewToolResponsesMessage,
  NewUserMessage,
} from "../types";
import type { ConversationMessagesServerMetadata } from "./conversationsSync/types";

// This file contains implementation of the Conversations service specific to managing conversations
// data and listing its metadata.

const logger = tagged("conversation-service/conversationService");

export type ConversationStorage = {
  conversations: {
    rows: { [key: string]: Conversation };
    /* provide a way to identify when the storage schema has changed */
    version: 1;
  };
  /* conversations logging is only enabled for development purposes */
  conversationsLogging: {
    isEnabled: boolean;
    rows: string[]; // JSON stringified ConversationLoggingRow
    /* provide a way to identify when the storage schema has changed */
    version: 1;
  };
  /* persisting the flag for whether diagnostics are enabled, dev only */
  conversationsDiagnostics: {
    isEnabled: boolean;
  };
};

export function createConversationService(
  storage: GuaranteedKVStorage<ConversationStorage>,
) {
  const conversationsSync = createConversationsSyncWithAdapters(storage);

  // Track active subscriptions per conversation to enable cancellation
  const activeSubscriptions = new Map<string, () => void>();

  const diagnosticsService = createConversationDiagnostics(storage);

  // TODO:(rich) refactor to processPostNewMessage
  // Gets called for each message in the queue
  async function processPostNewMessage(
    newMessage: NewUserMessage | NewToolResponsesMessage,
  ): Promise<void> {
    const {
      conversationId,
      model,
      agentPayload,
      attachments = [],
      agentIds = [],
      clientContext,
      source,
      searchParams,
      skipPersistence,
      fssSearchScope,
      stackId,
    } = newMessage;

    // Cancel any existing request for this conversation
    const existingUnsubscribe = activeSubscriptions.get(conversationId);
    if (existingUnsubscribe) {
      existingUnsubscribe();
      activeSubscriptions.delete(conversationId);
    }

    const conversation = await getConversation(conversationId);
    const logResponse = await getAddLoggingCallback();

    // Pending messages (includes new tool responses and new user/assistant message)
    const pendingMessages = createPendingMessages(newMessage);

    // Emit the pending message to the conversation subject right away
    // so that it renders in the UI immediately
    const inProgressConversationsResponseMessages =
      getInProgressConversationsResponseMessages(
        conversation.messages,
        pendingMessages,
      );
    conversationsSync.updateConversationMessages(
      conversationId,
      inProgressConversationsResponseMessages,
    );

    // TODO:(rich) we should pause this conversation from refreshing while the stream is ongoing
    // to avoid races where a pending message may be temporarily removed in the UI

    // Track the latest messages as they are streamed in from the GRPC endpoint
    // This is so we can update the local KV store with the final state once the stream completes
    let latestResult: ConversationsResult | undefined;

    // Use the GRPC endpoint to get streaming responses
    return new Promise<void>((resolve, reject) => {
      const experimentSetting = undefined;
      const modelChoice = model;
      const loggedConversationResponse =
        createLoggedConversationResponse(logResponse);

      const { historyMessages, messageText, rephrased, llmIgnoreUserMessage } =
        getPendingMessagesResponseArgs(conversation.messages, pendingMessages);
      const subscription = loggedConversationResponse
        .getResponse(
          messageText,
          conversation.serverConversationId,
          historyMessages,
          experimentSetting,
          source,
          modelChoice,
          agentPayload,
          attachments,
          agentIds,
          rephrased,
          clientContext || conversation.clientContext,
          searchParams,
          skipPersistence,
          fssSearchScope,
          llmIgnoreUserMessage,
          stackId,
        )
        .pipe(
          tap((result: ConversationsResult) => {
            // Store the latest messages every stream emission
            latestResult = result;

            if ("error" in result) {
              // no need to throw here, the error will be handled in the finalize
              return;
            }

            const messages = result.messages.map((message, index, array) =>
              // Convert the GRPC message to a ConversationMessage
              convertToConversationMessage(
                message,
                // If the message is the last one, we assume its the currently streaming message
                // otherwise, we mark it as success (since the API sends down the full history every request)
                index === array.length - 1 ? "streaming" : "success",
              ),
            );

            const jsonContent = parseMessageJSON(messages[messages.length - 1]);
            const markdownDraft = jsonContent?.content;
            conversationsSync.updateConversationMessages(
              conversationId,
              messages,
              {
                serverConversationId: result.serverConversationId,
                suggestedQueries: result.suggestedQueries,
                progressUpdates: result.progressUpdates,
                markdownDraft: markdownDraft || conversation.markdownDraft,
                assistRequestId: result.assistRequestId,
                spanId: result.spanId,
              },
              clientContext || conversation.clientContext,
              undefined, // no requestIdOverride
              stackId || conversation.stackId,
            );
          }),
          finalize(async () => {
            // When the stream completes, update local KV storage with the final state
            // using the latestMessages
            try {
              if (!latestResult) {
                throw new Error("No result from GRPC stream");
              }
              diagnosticsService.addDiagnosticsLog(latestResult);
              if ("error" in latestResult) {
                // Rethrow the original APIv2GRPCWebError, including the requestId if present
                const error = new Error(
                  latestResult.error,
                ) as APIv2GRPCWebError;
                error.dropboxRequestId = latestResult.requestId;
                throw error;
              }
              // mark last message as success when resolving
              const latestMessages = latestResult.messages.map((message) =>
                convertToConversationMessage(message, "success"),
              );

              // Parse response message for markdown draft (if exists)
              const jsonContent = parseMessageJSON(
                latestMessages[latestMessages.length - 1],
              );
              const markdownDraft = jsonContent?.content;

              // emit the final state to subscribers + save
              conversationsSync.updateConversationMessages(
                conversationId,
                latestMessages,
                {
                  serverConversationId: latestResult.serverConversationId,
                  suggestedQueries: latestResult.suggestedQueries,
                  progressUpdates: latestResult.progressUpdates,
                  markdownDraft: markdownDraft || conversation.markdownDraft,
                  assistRequestId: latestResult.assistRequestId,
                  spanId: latestResult.spanId,
                },
                clientContext || conversation.clientContext,
                undefined, // no requestIdOverride
                stackId || conversation.stackId,
              );

              // If this was the first message in the conversation, refresh the conversation list
              // to ensure the new conversation appears in the list
              const shouldRefresh =
                conversation.serverConversationId === undefined &&
                latestResult.serverConversationId !== undefined;
              if (shouldRefresh) {
                refreshConversationList();
              }

              loggedConversationResponse.logCompletion(latestResult);
              resolve();
            } catch (error) {
              const errorConversationsResponseMessages =
                getErrorConversationsResponseMessages(
                  conversation.messages,
                  pendingMessages,
                );
              conversationsSync.updateConversationMessages(
                conversationId,
                errorConversationsResponseMessages,
                undefined, // response
                undefined, // clientContext
                // If we recovered the request id, update the conversation
                (error as APIv2GRPCWebError).dropboxRequestId,
              );
              reject(error);
            }
          }),
        )
        .subscribe();

      const unsubscribe = () => {
        subscription.unsubscribe();
        activeSubscriptions.delete(conversationId);
      };
      activeSubscriptions.set(conversationId, unsubscribe);
      return unsubscribe;
    });
  }

  /**
   * Append manual messages to the conversation without submitting them to the API.
   * @param newMessages An array of new user messages to append.
   */
  async function appendManualMessages(
    newMessages: NewUserMessage[],
    replaceLastMessage?: boolean,
    metadata?: ConversationMessagesServerMetadata,
  ) {
    if (newMessages.length === 0) {
      logger.warn("appendManualMessages called with empty messages; ignoring");
      return;
    }

    const clientContext = newMessages[0].clientContext;
    const conversationId = newMessages[0].conversationId;
    const stackId = newMessages[0].stackId;
    const conversation = await getConversation(conversationId);

    const craftedMessages: ConversationMessage[] = newMessages.map(
      (newMessage) => {
        const { message, llmIgnore, attachments, messageType, rephrased } =
          newMessage;
        let messageTypePayload:
          | ConversationUserMessage
          | ConversationAssistantMessage;
        if (messageType === "assistantMessage") {
          messageTypePayload = {
            case: "assistantMessage",
            value: {
              text: message,
              cited: [],
              context: [],
              isCached: false,
            },
          };
        } else {
          messageTypePayload = {
            case: "userMessage",
            value: {
              text: message,
              rephrased: rephrased || "",
              attachments: attachments || [],
              llmIgnore: llmIgnore || false,
            },
          };
        }

        return {
          // manually crafted messages are missing an ID
          // so they are persisted with a generated ID on the server
          id: "",
          messageType: messageTypePayload,
          timestamp: new Date(),
          status: "success",
        };
      },
    );

    const previousMessages = [...conversation.messages];
    if (replaceLastMessage) previousMessages.pop();

    if (
      isEqualWith(
        previousMessages,
        craftedMessages,
        (objValue, othValue, key) => {
          // Ignore timestamp fields when comparing
          if (key === "timestamp") {
            return true;
          }
          // Use default comparison for all other fields
          return undefined;
        },
      )
    ) {
      return;
    }

    const historyWithPending = [...previousMessages, ...craftedMessages];
    conversationsSync.updateConversationMessages(
      conversationId,
      historyWithPending,
      metadata,
      clientContext || conversation.clientContext,
      undefined, // no requestIdOverride
      stackId || conversation.stackId,
    );
  }

  /**
   * Update the draft for a conversation. This updates the locally stored draft before
   * resolving the promise + will update the server draft afterwards.
   *
   * In cases where the conversation is newly created (`conversationsGetResponse` has not yet
   * been called), the draft will not be persisted to the server until after the first response.
   */
  function updateConversationDraft(conversationId: string, draft: string) {
    return conversationsSync.updateConversationDraft(conversationId, draft);
  }

  /**
   * Observe a conversation. This will emit the full conversation history as it changes.
   * @param conversationId The ID of the conversation to observe.
   * @returns An observable that emits the full conversation history.
   */
  function observeConversation(
    conversationId: string,
  ): Observable<Conversation> {
    return conversationsSync.getConversationObservable(conversationId);
  }

  /**
   * Get latest cached state of conversation. Call refreshConversation if we want to refresh from the server.
   * If it hasn't been loaded yet, this will trigger a load from the server.
   */
  async function getConversation(
    conversationId: string,
  ): Promise<Conversation> {
    return conversationsSync.getConversation(conversationId);
  }

  /**
   * Get a conversation by server conversation ID.
   */
  async function getConversationByServerId(
    serverConversationId: string,
  ): Promise<ConversationWithId> {
    return conversationsSync.getConversationByServerId(serverConversationId);
  }

  /**
   * Create a new conversation for a conversation that does not yet exist on the server.
   */
  async function createNewConversation(): Promise<ConversationWithId> {
    return conversationsSync.createNewConversation();
  }

  /**
   * Delete a list of conversations from the server
   */
  async function deleteConversations(conversationIDs: string[]): Promise<void> {
    await conversationsSync.deleteConversations(conversationIDs);
  }

  /**
   * Refresh the conversation from the server. This will not resolve until the conversation has been
   * refreshed from the server.
   */
  function refreshConversation(conversationId: string): void {
    conversationsSync.refreshConversation(conversationId);
  }

  /**
   * Get a list of all recent conversations.
   */
  async function getConversationList(): Promise<ConversationMetadata[]> {
    return conversationsSync.getConversationsList();
  }

  /**
   * Refresh the list of all recent conversations.
   */
  async function refreshConversationList(): Promise<ConversationMetadata[]> {
    return conversationsSync.refreshConversationsList();
  }

  /**
   * Get metadata for a specific conversation. This may return metadata that doesn't exist in
   * getConversationList, e.g. in case where the conversation was recently created; or if the
   * conversation was not recent.
   */
  async function getConversationMetadata(
    serverConversationId: string,
  ): Promise<ConversationMetadata | undefined> {
    return conversationsSync.getConversationMetadata(serverConversationId);
  }

  /**
   * Observe the list of all recent conversations as it changes.
   * @returns An observable that emits the list of all recent conversations.
   */
  function observeConversationList(): Observable<ConversationMetadata[]> {
    return conversationsSync.metadatasObservable();
  }

  /**
   * Post a new user message to the conversation.
   * @param newMessage The new user message to post.
   */
  async function postUserMessage(newMessage: NewUserMessage): Promise<void> {
    try {
      await processPostNewMessage(newMessage);
    } catch (error) {
      logger.error("Failed to process user message:", error);
      Sentry.withScope((scope) => {
        scope.setTag(
          "errorMessage",
          isErrorWithMessage(error) ? error.message : "unknown",
        );
        Sentry.captureException(error, {}, scope);
        Sentry.captureMessage(
          "[service-conversation] error processing user message",
          "error",
          {},
          scope,
        );
      });
    }
  }

  /**
   * Post a new tool responses message to the conversation.
   * This will generate a new response from the server based on the tool responses.
   */
  async function postToolResponses(
    toolResponses: NewToolResponsesMessage,
  ): Promise<void> {
    try {
      await processPostNewMessage(toolResponses);
    } catch (error) {
      logger.error("Failed to process tool responses:", error);
      Sentry.withScope((scope) => {
        scope.setTag(
          "errorMessage",
          isErrorWithMessage(error) ? error.message : "unknown",
        );
        Sentry.captureException(error, {}, scope);
        Sentry.captureMessage(
          "[service-conversation] error processing tool responses",
          "error",
          {},
          scope,
        );
      });
    }
  }

  /**
   * Clear all messages from a conversation without deleting the conversation itself.
   * @param conversationId The ID of the conversation to clear.
   */
  function clearConversation(conversationId: string) {
    conversationsSync.updateConversationMessages(conversationId, []);
  }

  /**
   * Set whether conversation logging is enabled.
   * @param enabled Whether to enable conversation logging.
   */
  async function setConversationLoggingEnabled(enabled: boolean) {
    const loggingStorage = await storage.get("conversationsLogging");
    storage.set("conversationsLogging", {
      ...loggingStorage,
      isEnabled: enabled,
    });
  }

  /**
   * Get whether conversation logging is enabled.
   * @returns Whether conversation logging is enabled.
   */
  async function getConversationLoggingEnabled(): Promise<boolean> {
    const loggingStorage = await storage.get("conversationsLogging");
    return loggingStorage?.isEnabled ?? false;
  }

  /**
   * Get all conversation logging rows.
   * @returns All conversation logging rows.
   */
  async function getConversationLoggingRows(): Promise<string[]> {
    const loggingStorage = await storage.get("conversationsLogging");
    return loggingStorage?.rows ?? [];
  }

  /**
   * Clear all conversation logging rows.
   */
  async function clearConversationLoggingRows() {
    const loggingStorage = await storage.get("conversationsLogging");
    storage.set("conversationsLogging", {
      ...loggingStorage,
      rows: [],
    });
  }

  // Returns a callback that can be used to add a row to the conversation logging storage.
  // If logging is disabled, the callback will be undefined.
  async function getAddLoggingCallback() {
    const isEnabled = await getConversationLoggingEnabled();
    if (!isEnabled) {
      return undefined;
    }
    return async (row: string) => {
      const loggingStorage = await storage.get("conversationsLogging");
      const rows = loggingStorage.rows;
      rows.push(row);
      storage.set("conversationsLogging", {
        ...loggingStorage,
        rows,
      });
    };
  }

  /**
   * Cancel any ongoing request for a specific conversation.
   * @param conversationId The ID of the conversation to cancel.
   * @returns true if active request was found/cancelled, false if no active request.
   */
  function cancelConversationRequest(conversationId: string): boolean {
    const unsubscribe = activeSubscriptions.get(conversationId);
    if (unsubscribe) {
      unsubscribe();
      return true;
    }
    return false;
  }

  return {
    // Conversations Data
    postUserMessage,
    postToolResponses,
    observeConversation,
    appendManualMessages,
    updateConversationDraft,
    getConversation,
    getConversationByServerId,
    createNewConversation,
    refreshConversation,
    deleteConversations,
    clearConversation,
    cancelConversationRequest,

    // Conversations Metadata
    getConversationList,
    refreshConversationList,
    getConversationMetadata,
    observeConversationList,

    // Conversations Logging (DEV ONLY)
    setConversationLoggingEnabled,
    getConversationLoggingEnabled,
    getConversationLoggingRows,
    clearConversationLoggingRows,

    // Diagnostics (DEV ONLY)
    ...diagnosticsService,
  };
}
